import React from "react";
import { Link } from "react-router-dom";
import lunalogo from "./lunatarot_logo.png";
import { trackStoreClick } from "./firebaseAnalytics";

type Locale = "ko" | "en" | "ja" | "es" | "fr" | "pt_BR" | "vi";

const LOCALE_STORAGE_KEY = "lunatarot-landing-locale";

const localeLabels: Record<Locale, string> = {
    ko: "KO",
    en: "EN",
    ja: "JA",
    es: "ES",
    fr: "FR",
    pt_BR: "PT",
    vi: "VI",
};

const content: Record<
    Locale,
    {
        appName: string;
        navAbout: string;
        navTerms: string;
        navPrivacy: string;
        overline: string;
        title: string[];
        lead: string;
        appStore: string;
        googlePlay: string;
        note: string;
        visualBody: string;
        cards: Array<{ eyebrow: string; title: string; body: string }>;
        footerAbout: string;
        footerTerms: string;
        footerPrivacy: string;
    }
> = {
    ko: {
        appName: "Luna : AI 감정일기",
        navAbout: "앱 소개",
        navTerms: "이용약관",
        navPrivacy: "개인정보 처리방침",
        overline: "Now on iPhone and Android",
        title: ["감정을 기록하고", "타로로 돌아보는 저널"],
        lead:
            "오늘의 기분과 생각을 적고, 빈티지 골드 타로 카드와 함께 감정을 쌓아가는 개인 저널 앱. 기록이 쌓일수록 AI가 감정의 흐름을 분석해 드립니다.",
        appStore: "App Store에서 보기",
        googlePlay: "Google Play에서 보기",
        note:
            "회원가입 없이 바로 사용할 수 있으며, 저널 기록은 기기 내에 저장됩니다. AI 분석을 사용할 때만 작성한 내용과 카드 정보가 외부 API로 전송될 수 있습니다.",
        visualBody:
            "밤하늘 톤 위에 황금선으로 그려낸 78장의 오리지널 타로 카드 덱. 저널에 카드를 더해 하루를 더 깊이 돌아보세요.",
        cards: [
            {
                eyebrow: "Journal",
                title: "감정과 타로를 함께 기록",
                body: "매일의 기분을 선택하고 생각을 적은 뒤, 오늘과 어울리는 타로 카드를 함께 남겨보세요. 기록은 모두 기기 안에 저장됩니다.",
            },
            {
                eyebrow: "AI Analysis",
                title: "감정 흐름을 읽어주는 AI",
                body: "쌓인 저널을 바탕으로 AI가 감정의 패턴과 흐름을 분석해 드립니다. 내가 놓쳤던 변화를 발견할 수 있어요.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "타로 스프레드 (Android 전용)",
                body: "오늘의 카드, 3장 리딩 등 다양한 타로 스프레드로 깊이 있는 해석을 즐길 수 있습니다. 현재 Android에서만 제공됩니다.",
            },
        ],
        footerAbout: "상세 소개",
        footerTerms: "이용약관",
        footerPrivacy: "개인정보 처리방침",
    },
    en: {
        appName: "Luna : AI Emotion Journal",
        navAbout: "About",
        navTerms: "Terms",
        navPrivacy: "Privacy Policy",
        overline: "Now on iPhone and Android",
        title: ["An emotion journal", "with a tarot perspective"],
        lead:
            "Record your daily mood and thoughts alongside a vintage gold tarot card. As your journal grows, AI surfaces the emotional patterns you might have missed.",
        appStore: "View on the App Store",
        googlePlay: "View on Google Play",
        note:
            "No account required. Your journal stays on your device. Only when you use AI analysis may your entry and card data be sent to an external API.",
        visualBody:
            "An original 78-card deck drawn in luminous gold lines over a quiet midnight palette. Attach a card to each journal entry and reflect deeper.",
        cards: [
            {
                eyebrow: "Journal",
                title: "Log emotions with tarot",
                body: "Pick your mood, write your thoughts, and attach a tarot card that fits the day. Everything stays on your device.",
            },
            {
                eyebrow: "AI Analysis",
                title: "Understand your emotional flow",
                body: "AI reads through your journal history to analyze patterns and shifts in your emotions over time.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "Tarot spreads (Android only)",
                body: "Daily draws, 3-card spreads, and more are available for deeper readings. Currently available on Android only.",
            },
        ],
        footerAbout: "Detailed About",
        footerTerms: "Terms",
        footerPrivacy: "Privacy Policy",
    },
    ja: {
        appName: "Luna : AI 感情日記",
        navAbout: "アプリ紹介",
        navTerms: "利用規約",
        navPrivacy: "プライバシーポリシー",
        overline: "Now on iPhone and Android",
        title: ["感情を記録して", "タロットで振り返るジャーナル"],
        lead:
            "今日の気分と想いをタロットカードとともに残す、個人ジャーナルアプリ。記録が積み重なるほど、AI が感情の流れを読み解いてくれます。",
        appStore: "App Storeで見る",
        googlePlay: "Google Playで見る",
        note:
            "アカウント登録不要。ジャーナルはすべて端末内に保存されます。AI 分析を使う場合に限り、記録とカード情報が外部 API に送信されることがあります。",
        visualBody:
            "静かな夜の雰囲気の上に、金色の線で描かれた 78 枚のオリジナルデッキ。毎日の記録にカードを添えて、感情をより深く振り返れます。",
        cards: [
            {
                eyebrow: "Journal",
                title: "感情とタロットを一緒に記録",
                body: "今日の気分を選んで言葉にしたら、その日に合ったタロットカードを添えて保存できます。記録はすべて端末内に保管されます。",
            },
            {
                eyebrow: "AI Analysis",
                title: "感情の流れを読み解く AI",
                body: "蓄積されたジャーナルをもとに、AI が感情のパターンと変化を分析します。見落としていた自分の変化に気づけます。",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "タロットスプレッド（Android のみ）",
                body: "今日の一枚、3 枚引きなど、さまざまなスプレッドで深い解釈を楽しめます。現在 Android のみで提供しています。",
            },
        ],
        footerAbout: "詳しい紹介",
        footerTerms: "利用規約",
        footerPrivacy: "プライバシーポリシー",
    },
    es: {
        appName: "Luna : Diario Emocional IA",
        navAbout: "Sobre la app",
        navTerms: "Términos",
        navPrivacy: "Política de privacidad",
        overline: "Now on iPhone and Android",
        title: ["Un diario emocional", "con perspectiva del tarot"],
        lead:
            "Registra tu estado de ánimo y pensamientos junto a una carta de tarot vintage dorada. Con el tiempo, la IA revela los patrones emocionales que quizás no habías notado.",
        appStore: "Ver en el App Store",
        googlePlay: "Ver en Google Play",
        note:
            "No se requiere cuenta. Tu diario se guarda en tu dispositivo. Solo al usar el análisis de IA, tu entrada y datos de cartas pueden enviarse a una API externa.",
        visualBody:
            "Un mazo original de 78 cartas dibujadas en líneas doradas sobre una paleta de medianoche. Adjunta una carta a cada entrada del diario y reflexiona más profundamente.",
        cards: [
            {
                eyebrow: "Journal",
                title: "Registra emociones con tarot",
                body: "Elige tu estado de ánimo, escribe tus pensamientos y adjunta una carta de tarot que encaje con el día. Todo se guarda en tu dispositivo.",
            },
            {
                eyebrow: "AI Analysis",
                title: "Comprende tu flujo emocional",
                body: "La IA analiza tu historial de diario para detectar patrones y cambios en tus emociones a lo largo del tiempo.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "Tiradas de tarot (solo Android)",
                body: "Cartas diarias, tiradas de 3 cartas y más para lecturas más profundas. Disponible actualmente solo en Android.",
            },
        ],
        footerAbout: "Más información",
        footerTerms: "Términos",
        footerPrivacy: "Política de privacidad",
    },
    fr: {
        appName: "Luna : Journal Émotionnel IA",
        navAbout: "À propos",
        navTerms: "Conditions",
        navPrivacy: "Politique de confidentialité",
        overline: "Now on iPhone and Android",
        title: ["Un journal émotionnel", "avec une perspective tarot"],
        lead:
            "Notez votre humeur et vos pensées aux côtés d'une carte de tarot vintage dorée. Au fil du temps, l'IA révèle les schémas émotionnels que vous n'aviez peut-être pas remarqués.",
        appStore: "Voir sur l'App Store",
        googlePlay: "Voir sur Google Play",
        note:
            "Aucun compte requis. Votre journal reste sur votre appareil. L'IA peut envoyer vos données à une API externe uniquement lors de l'analyse.",
        visualBody:
            "Un jeu original de 78 cartes dessinées en lignes dorées sur une palette minuit. Joignez une carte à chaque entrée du journal pour une réflexion plus profonde.",
        cards: [
            {
                eyebrow: "Journal",
                title: "Enregistrez vos émotions avec le tarot",
                body: "Choisissez votre humeur, écrivez vos pensées et joignez une carte de tarot adaptée à la journée. Tout reste sur votre appareil.",
            },
            {
                eyebrow: "AI Analysis",
                title: "Comprenez votre flux émotionnel",
                body: "L'IA analyse votre historique de journal pour détecter les schémas et les changements dans vos émotions au fil du temps.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "Tirages de tarot (Android uniquement)",
                body: "Tirages quotidiens, tirages à 3 cartes et plus pour des lectures approfondies. Disponible uniquement sur Android.",
            },
        ],
        footerAbout: "En savoir plus",
        footerTerms: "Conditions",
        footerPrivacy: "Politique de confidentialité",
    },
    pt_BR: {
        appName: "Luna : Diário Emocional IA",
        navAbout: "Sobre o app",
        navTerms: "Termos",
        navPrivacy: "Política de privacidade",
        overline: "Now on iPhone and Android",
        title: ["Um diário emocional", "com perspectiva do tarot"],
        lead:
            "Registre seu humor e pensamentos ao lado de uma carta de tarô vintage dourada. Com o tempo, a IA revela os padrões emocionais que você pode ter perdido.",
        appStore: "Ver na App Store",
        googlePlay: "Ver no Google Play",
        note:
            "Nenhuma conta necessária. Seu diário fica no seu dispositivo. Apenas ao usar a análise de IA, sua entrada e dados de cartas podem ser enviados a uma API externa.",
        visualBody:
            "Um baralho original de 78 cartas desenhadas em linhas douradas sobre uma paleta meia-noite. Anexe uma carta a cada entrada do diário e reflita mais profundamente.",
        cards: [
            {
                eyebrow: "Journal",
                title: "Registre emoções com tarô",
                body: "Escolha seu humor, escreva seus pensamentos e anexe uma carta de tarô que se encaixe no dia. Tudo fica no seu dispositivo.",
            },
            {
                eyebrow: "AI Analysis",
                title: "Entenda seu fluxo emocional",
                body: "A IA analisa seu histórico de diário para detectar padrões e mudanças nas suas emoções ao longo do tempo.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "Tiragens de tarô (somente Android)",
                body: "Cartas diárias, tiragens de 3 cartas e mais para leituras mais profundas. Disponível atualmente somente no Android.",
            },
        ],
        footerAbout: "Saiba mais",
        footerTerms: "Termos",
        footerPrivacy: "Política de privacidade",
    },
    vi: {
        appName: "Luna : Nhật Ký Cảm Xúc AI",
        navAbout: "Giới thiệu",
        navTerms: "Điều khoản",
        navPrivacy: "Chính sách bảo mật",
        overline: "Now on iPhone and Android",
        title: ["Nhật ký cảm xúc", "qua lăng kính tarot"],
        lead:
            "Ghi lại tâm trạng và suy nghĩ hàng ngày cùng với một lá bài tarot vàng cổ điển. Khi nhật ký tích lũy, AI sẽ phát hiện những xu hướng cảm xúc mà bạn có thể đã bỏ lỡ.",
        appStore: "Xem trên App Store",
        googlePlay: "Xem trên Google Play",
        note:
            "Không cần tài khoản. Nhật ký được lưu trên thiết bị của bạn. Chỉ khi sử dụng phân tích AI, dữ liệu nhật ký và lá bài mới có thể được gửi đến API bên ngoài.",
        visualBody:
            "Bộ bài gốc 78 lá được vẽ bằng đường nét vàng trên nền màu nửa đêm. Đính kèm một lá bài vào mỗi mục nhật ký để suy ngẫm sâu hơn.",
        cards: [
            {
                eyebrow: "Journal",
                title: "Ghi cảm xúc cùng tarot",
                body: "Chọn tâm trạng, viết suy nghĩ và đính kèm lá tarot phù hợp với ngày hôm nay. Tất cả được lưu trên thiết bị của bạn.",
            },
            {
                eyebrow: "AI Analysis",
                title: "Hiểu dòng chảy cảm xúc",
                body: "AI phân tích lịch sử nhật ký để tìm ra các xu hướng và thay đổi cảm xúc theo thời gian.",
            },
            {
                eyebrow: "Tarot Spread · Android",
                title: "Trải bài tarot (chỉ Android)",
                body: "Rút bài hàng ngày, trải 3 lá và nhiều hơn nữa cho những buổi đọc sâu hơn. Hiện chỉ có trên Android.",
            },
        ],
        footerAbout: "Xem thêm",
        footerTerms: "Điều khoản",
        footerPrivacy: "Chính sách bảo mật",
    },
};

const detectLocale = (): Locale => {
    if (typeof window === "undefined") {
        return "en";
    }

    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "ko" || stored === "en" || stored === "ja") {
        return stored;
    }

    const browserLocales = window.navigator.languages?.length
        ? window.navigator.languages
        : [window.navigator.language];

    for (const locale of browserLocales) {
        const normalized = locale.toLowerCase();
        if (normalized.startsWith("ko")) return "ko";
        if (normalized.startsWith("ja")) return "ja";
        if (normalized.startsWith("es")) return "es";
        if (normalized.startsWith("fr")) return "fr";
        if (normalized.startsWith("pt")) return "pt_BR";
        if (normalized.startsWith("vi")) return "vi";
        if (normalized.startsWith("en")) return "en";
    }

    return "en";
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: "100vh",
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Noto Sans KR', 'Noto Sans JP', 'Noto Sans', Arial, sans-serif",
        color: "var(--ink)",
        background: "linear-gradient(180deg, #f0edfe 0%, #ffffff 36%)",
        overflow: "hidden",
    },
    shell: {
        width: "100%",
        maxWidth: 1120,
        margin: "0 auto",
        padding: "28px 20px 64px",
        boxSizing: "border-box",
    },
    nav: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        marginBottom: 32,
        flexWrap: "wrap",
    },
    navBrand: {
        color: "var(--lav-deep)",
        textDecoration: "none",
        fontSize: 14,
        letterSpacing: 1.2,
        textTransform: "uppercase",
        fontWeight: 800,
    },
    navActions: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
    },
    navLinks: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 14,
    },
    navLink: {
        color: "var(--ink-2)",
        textDecoration: "none",
        fontWeight: 600,
    },
    localeSwitch: {
        display: "inline-flex",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: "#fff",
        border: "1px solid var(--rule-soft)",
    },
    localeButton: {
        minWidth: 42,
        padding: "8px 10px",
        borderRadius: 999,
        border: "none",
        background: "transparent",
        color: "var(--ink-3)",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
    },
    activeLocaleButton: {
        background: "var(--luna)",
        color: "#fff",
        boxShadow: "0 6px 18px rgba(156, 140, 242, 0.35)",
    },
    hero: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: 28,
        alignItems: "center",
        padding: "8px 0 28px",
    },
    heroCopy: {
        textAlign: "left",
    },
    overline: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: "rgba(156, 140, 242, 0.12)",
        border: "1px solid rgba(156, 140, 242, 0.22)",
        color: "var(--lav-deep)",
        fontSize: 12,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        fontWeight: 700,
    },
    brandTitle: {
        margin: "18px 0 6px",
        fontSize: "clamp(2.1rem, 5vw, 3.6rem)",
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontWeight: 800,
        color: "var(--ink)",
    },
    title: {
        margin: "0 0 14px",
        fontSize: "clamp(2rem, 5.2vw, 3.7rem)",
        lineHeight: 1.08,
        letterSpacing: "-0.03em",
        fontWeight: 800,
        color: "var(--lav-deep)",
    },
    lead: {
        margin: "0 0 18px",
        fontSize: 16,
        lineHeight: 1.8,
        color: "var(--ink-2)",
        maxWidth: 560,
    },
    buttonRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        marginTop: 24,
    },
    primaryButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 50,
        padding: "14px 20px",
        borderRadius: 999,
        background: "linear-gradient(135deg, #ab9df6 0%, #7a68d8 100%)",
        color: "#fff",
        textDecoration: "none",
        fontSize: 15,
        fontWeight: 700,
        boxShadow: "0 14px 30px rgba(122, 104, 216, 0.3)",
        flex: "1 1 180px",
    },
    secondaryButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 50,
        padding: "14px 20px",
        borderRadius: 999,
        background: "#fff",
        border: "1.5px solid var(--rule)",
        color: "var(--ink)",
        textDecoration: "none",
        fontSize: 15,
        fontWeight: 700,
        boxShadow: "0 14px 30px rgba(31, 42, 68, 0.08)",
        flex: "1 1 180px",
    },
    note: {
        marginTop: 16,
        fontSize: 13,
        lineHeight: 1.7,
        color: "var(--ink-3)",
    },
    heroVisualWrap: {
        position: "relative",
        minHeight: 460,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    halo: {
        position: "absolute",
        width: 360,
        height: 360,
        borderRadius: "50%",
        background:
            "radial-gradient(circle, rgba(255, 215, 130, 0.26) 0%, rgba(255, 215, 130, 0.06) 42%, rgba(255, 215, 130, 0) 74%)",
        filter: "blur(4px)",
    },
    heroCard: {
        position: "relative",
        width: "min(100%, 430px)",
        padding: "28px 24px",
        borderRadius: 28,
        background:
            "linear-gradient(180deg, rgba(26, 19, 55, 0.94) 0%, rgba(8, 7, 25, 0.96) 100%)",
        border: "1px solid rgba(255, 222, 159, 0.16)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.42)",
        overflow: "hidden",
    },
    stars: {
        position: "absolute",
        inset: 0,
        backgroundImage:
            "radial-gradient(circle at 14% 18%, rgba(255,255,255,0.72) 0 1.5px, transparent 1.6px), radial-gradient(circle at 74% 14%, rgba(255,255,255,0.65) 0 1.4px, transparent 1.5px), radial-gradient(circle at 80% 34%, rgba(255,232,180,0.72) 0 1.8px, transparent 1.9px), radial-gradient(circle at 18% 72%, rgba(255,232,180,0.54) 0 1.4px, transparent 1.5px), radial-gradient(circle at 68% 78%, rgba(255,255,255,0.5) 0 1.2px, transparent 1.3px)",
        opacity: 0.6,
        pointerEvents: "none",
    },
    logoWrap: {
        position: "relative",
        zIndex: 1,
        display: "flex",
        justifyContent: "center",
        marginBottom: 18,
    },
    logo: {
        width: 196,
        height: 196,
        objectFit: "contain",
        filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.38))",
    },
    visualText: {
        position: "relative",
        zIndex: 1,
        textAlign: "center",
    },
    visualTitle: {
        margin: "0 0 10px",
        color: "#FFE3A4",
        fontSize: 28,
        letterSpacing: 0.4,
    },
    visualBody: {
        margin: "0 auto",
        maxWidth: 300,
        lineHeight: 1.7,
        fontSize: 14,
        color: "rgba(247, 238, 211, 0.84)",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
        marginTop: 16,
    },
    infoCard: {
        background: "#fff",
        border: "1px solid var(--rule-soft)",
        borderRadius: 20,
        padding: "18px 18px 16px",
        textAlign: "left",
        boxShadow: "0 10px 28px rgba(31, 42, 68, 0.07)",
    },
    cardEyebrow: {
        margin: "0 0 8px",
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "var(--lav-deep)",
        fontWeight: 700,
    },
    cardTitle: {
        margin: "0 0 8px",
        fontSize: 18,
        fontWeight: 800,
        color: "var(--ink)",
    },
    cardText: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.7,
        color: "var(--ink-2)",
    },
    screenshots: {
        marginTop: 48,
    },
    screenshotsLabel: {
        textAlign: "center",
        fontSize: 12,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: "var(--lav-deep)",
        fontWeight: 700,
        margin: "0 0 20px",
    },
    screenshotRow: {
        display: "flex",
        gap: 16,
        justifyContent: "center",
        overflowX: "auto",
        padding: "4px 0 12px",
    },
    screenshotImg: {
        width: 180,
        flexShrink: 0,
        borderRadius: 20,
        border: "1px solid var(--rule-soft)",
        boxShadow: "0 16px 40px rgba(31, 42, 68, 0.14)",
    },
    footer: {
        marginTop: 42,
        paddingTop: 18,
        borderTop: "1px solid var(--rule-soft)",
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
        color: "var(--ink-3)",
        fontSize: 13,
    },
};

const LunaTarotLanding: React.FC = () => {
    const [locale, setLocale] = React.useState<Locale>("en");

    const handleStoreClick = React.useCallback((storeName: "app_store" | "google_play") => () => {
        trackStoreClick({
            app_name: "lunatarot",
            store_name: storeName,
            source_page: "lunatarot_landing",
        });
    }, []);

    React.useEffect(() => {
        setLocale(detectLocale());
    }, []);

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        }
    }, [locale]);

    const copy = content[locale];

    return (
        <main style={styles.page}>
            <div style={styles.shell}>
                <nav style={styles.nav}>
                    <Link to="/" style={styles.navBrand}>
                        Daydream Factory
                    </Link>
                    <div style={styles.navActions}>
                        <div style={styles.localeSwitch} aria-label="Language switcher">
                            {(Object.keys(localeLabels) as Locale[]).map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => setLocale(option)}
                                    style={{
                                        ...styles.localeButton,
                                        ...(locale === option ? styles.activeLocaleButton : {}),
                                    }}
                                >
                                    {localeLabels[option]}
                                </button>
                            ))}
                        </div>
                        <div style={styles.navLinks}>
                            <Link to="/lunatarot/about" style={styles.navLink}>
                                {copy.navAbout}
                            </Link>
                            <Link to="/lunatarot/terms" style={styles.navLink}>
                                {copy.navTerms}
                            </Link>
                            <Link to="/lunatarot/privacy" style={styles.navLink}>
                                {copy.navPrivacy}
                            </Link>
                        </div>
                    </div>
                </nav>

                <section style={styles.hero}>
                    <div style={styles.heroCopy}>
                        <span style={styles.overline}>{copy.overline}</span>
                        <h1 style={styles.brandTitle}>{copy.appName}</h1>
                        <h2 style={styles.title}>
                            {copy.title[0]}
                            <br />
                            {copy.title[1]}
                        </h2>
                        <p style={styles.lead}>{copy.lead}</p>

                        <div style={styles.buttonRow}>
                            <a
                                href="https://apps.apple.com/app/id6755215945"
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleStoreClick("app_store")}
                                style={styles.primaryButton}
                            >
                                {copy.appStore}
                            </a>
                            <a
                                href="https://play.google.com/store/apps/details?id=net.daydreamfactory.lunatarot"
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleStoreClick("google_play")}
                                style={styles.secondaryButton}
                            >
                                {copy.googlePlay}
                            </a>
                        </div>

                        <p style={styles.note}>{copy.note}</p>
                    </div>

                    <div style={styles.heroVisualWrap}>
                        <div style={styles.halo} />
                        <div style={styles.heroCard}>
                            <div style={styles.stars} />
                            <div style={styles.logoWrap}>
                                <img src={lunalogo} alt="LunaTarot logo" style={styles.logo} />
                            </div>
                            <div style={styles.visualText}>
                                <h2 style={styles.visualTitle}>Mystic Vintage Gold Tarot</h2>
                                <p style={styles.visualBody}>{copy.visualBody}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={styles.screenshots}>
                    <p style={styles.screenshotsLabel}>Preview</p>
                    <div style={styles.screenshotRow}>
                        {[1, 2, 4].map((n) => (
                            <img
                                key={n}
                                src={`/reference/${locale}/${n}.png`}
                                alt={`App screenshot ${n}`}
                                style={styles.screenshotImg}
                            />
                        ))}
                    </div>
                </section>

                <section style={styles.grid}>
                    {copy.cards.map((card) => (
                        <article key={card.title} style={styles.infoCard}>
                            <p style={styles.cardEyebrow}>{card.eyebrow}</p>
                            <h3 style={styles.cardTitle}>{card.title}</h3>
                            <p style={styles.cardText}>{card.body}</p>
                        </article>
                    ))}
                </section>

                <footer style={styles.footer}>
                    <span>support@daydreamfactory.net</span>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <Link to="/lunatarot/about" style={styles.navLink}>
                            {copy.footerAbout}
                        </Link>
                        <Link to="/lunatarot/terms" style={styles.navLink}>
                            {copy.footerTerms}
                        </Link>
                        <Link to="/lunatarot/privacy" style={styles.navLink}>
                            {copy.footerPrivacy}
                        </Link>
                    </div>
                </footer>
            </div>
        </main>
    );
};

export default LunaTarotLanding;
